import type { LinkScenarioMissionMode } from 'components/NormalizedMissionHistoryPanel/NormalizedMissionHistoryPanel'
import { GAUSIUM_ENABLED } from 'utils/constants'
import { VENDOR, type VendorCode } from 'utils/vendorDisplayName'

/**
 * Single UI profile for vendor-specific tabs on Facility.
 * `effectiveVendor` is either the selected vendor in the mixed-fleet dropdown
 * or the actual vendor for a single-vendor facility.
 */
export type FacilityVendorUiLayout = {
  vendor: VendorCode
  cleaning: {
    showAutonomicsScheduledTasks: boolean
    showGausiumExecutableMirrorRows: boolean
  }
  scenarios: {
    /** true → GausiumScenariosTabContent; false → ScenarioTable + MainApi.fetchScenarios */
    isGausiumMapsMode: boolean
  }
  history: {
    showStatusColumn: boolean
    linkScenarioMissionToPages: LinkScenarioMissionMode
    showGausiumReportActions: boolean
  }
  map: {
    /** Gausium geo pins on Scenarios/History when data exists and GAUSIUM_ENABLED is on. */
    showGausiumMapPinsOnScenariosHistory: boolean
  }
  tabs: {
    showScenariosTab: boolean
    showHistoryTab: boolean
  }
  /** Asbis: robots + map pins only (no cleaning/scenarios/history content). */
  isAsbisMinimalProfile: boolean
}

export type FacilityVendorUiLayoutOptions = {
  /** Facility has robots from more than one vendor — keep full tabs; dropdown picks content vendor. */
  isMixedVendorFacility?: boolean
}

export function getFacilityVendorUiLayout(
  effectiveVendor: VendorCode,
  options: FacilityVendorUiLayoutOptions = {}
): FacilityVendorUiLayout {
  const isMixedVendorFacility = options.isMixedVendorFacility ?? false
  const isGausium = effectiveVendor === VENDOR.GAUSIUM
  const isAsbis = effectiveVendor === VENDOR.ASBIS
  /** Minimal Asbis UI only when the facility fleet is Asbis-only (not mixed). */
  const isAsbisMinimalProfile = isAsbis && !isMixedVendorFacility

  return {
    vendor: effectiveVendor,
    cleaning: {
      showAutonomicsScheduledTasks: !isGausium && !isAsbis,
      showGausiumExecutableMirrorRows: isGausium,
    },
    scenarios: {
      isGausiumMapsMode: isGausium,
    },
    history: {
      showStatusColumn: !isGausium && !isAsbis,
      linkScenarioMissionToPages: isGausium ? 'gausium_only' : isAsbis ? false : true,
      showGausiumReportActions: GAUSIUM_ENABLED && isGausium,
    },
    map: {
      showGausiumMapPinsOnScenariosHistory: isGausium,
    },
    tabs: {
      showScenariosTab: !isAsbisMinimalProfile,
      showHistoryTab: !isAsbisMinimalProfile,
    },
    isAsbisMinimalProfile,
  }
}
